import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Chip, CircularProgress, Alert, useTheme,
} from '@mui/material';
import {
  Timeline, TimelineItem, TimelineSeparator, TimelineDot, TimelineConnector,
  TimelineContent, TimelineOppositeContent,
} from '@mui/lab';
import { useApp } from '../context/AppContext';
import { auditApi, AuditEntry } from '../api/audit';

const CHANGE_TYPE_COLORS: Record<string, 'success' | 'error' | 'info' | 'warning'> = {
  create: 'success',
  delete: 'error',
  update: 'info',
  env_toggle: 'warning',
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  create: 'Created',
  delete: 'Deleted',
  update: 'Updated',
  env_toggle: 'Env Toggle',
};

/** Render a diff value smartly — objects, booleans, null, primitives */
function DiffValue({ value, color }: { value: unknown; color: string }) {
  if (value === null || value === undefined) {
    return <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>—</Typography>;
  }
  if (typeof value === 'boolean') {
    return <Typography component="span" variant="caption" sx={{ color, fontWeight: 700 }}>{value ? 'ON' : 'OFF'}</Typography>;
  }
  if (typeof value === 'object') {
    // JSON for complex objects
    return <Typography component="span" variant="caption" sx={{ color, fontFamily: 'monospace', wordBreak: 'break-all' }}>{JSON.stringify(value)}</Typography>;
  }
  return <Typography component="span" variant="caption" sx={{ color }}>{String(value)}</Typography>;
}

/** For env_toggle: show only the specific envKey(s) that changed */
function EnvToggleDiff({ before, after }: { before: unknown; after: unknown }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const beforeMap = (before ?? {}) as Record<string, boolean>;
  const afterMap = (after ?? {}) as Record<string, boolean>;

  const changedKeys = Object.keys({ ...beforeMap, ...afterMap }).filter(
    k => beforeMap[k] !== afterMap[k]
  );

  if (changedKeys.length === 0) {
    return <Typography variant="caption" color="text.secondary">No changes</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {changedKeys.map(envKey => {
        const wasOn = beforeMap[envKey] ?? false;
        const isOn = afterMap[envKey] ?? false;
        return (
          <Box key={envKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', minWidth: 60 }}>
              {envKey}
            </Typography>
            <Box sx={{
              px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700,
              bgcolor: wasOn ? 'rgba(52,199,89,0.1)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              color: wasOn ? '#34C759' : 'text.secondary',
            }}>
              {wasOn ? 'ON' : 'OFF'}
            </Box>
            <Typography variant="caption" color="text.secondary">→</Typography>
            <Box sx={{
              px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700,
              bgcolor: isOn ? 'rgba(52,199,89,0.1)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
              color: isOn ? '#34C759' : 'text.secondary',
            }}>
              {isOn ? 'ON' : 'OFF'}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function DiffRow({ field, before, after, changeType }: {
  field: string;
  before: unknown;
  after: unknown;
  changeType: string;
}) {
  const theme = useTheme();

  // Special rendering for environments field
  if (field === 'environments') {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          environments
        </Typography>
        <EnvToggleDiff before={before} after={after} />
      </Box>
    );
  }

  // create/delete: just show the summary, skip raw flag dump
  if (field === 'flag') {
    return null;
  }

  return (
    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{field}:</Typography>
      <DiffValue value={before} color={theme.palette.error.main} />
      <Typography variant="caption" color="text.secondary">→</Typography>
      <DiffValue value={after} color={theme.palette.success.main} />
    </Box>
  );
}

export default function AuditLogPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { projectId } = useParams<{ projectId: string }>();
  const { selectProject } = useApp();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';

  useEffect(() => {
    if (projectId) {
      selectProject(projectId);
      setLoading(true);
      auditApi.list(projectId)
        .then(setEntries)
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [projectId, selectProject]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} mb={3} letterSpacing="-0.02em">Audit Log</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {entries.length === 0 ? (
        <Typography color="text.secondary">No audit entries yet.</Typography>
      ) : (
        <Timeline position="right" sx={{ p: 0 }}>
          {entries.map((entry, i) => (
            <TimelineItem key={`${entry.timestamp}-${entry.flagId}`}>
              <TimelineOppositeContent sx={{ flex: '0 0 140px', py: '14px', px: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                  {new Date(entry.timestamp).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                  {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot color={CHANGE_TYPE_COLORS[entry.changeType] ?? 'grey'} sx={{ my: 1.5 }} />
                {i < entries.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ py: '10px', px: 2 }}>
                <Card elevation={0} sx={{ border: `1px solid ${borderColor}`, borderRadius: 2.5, p: 2 }}>
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={CHANGE_TYPE_LABELS[entry.changeType] ?? entry.changeType}
                      size="small"
                      color={CHANGE_TYPE_COLORS[entry.changeType] ?? 'default'}
                      sx={{ fontWeight: 700, fontSize: 11 }}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {entry.flagKey}
                    </Typography>
                  </Box>

                  {/* Operator */}
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    by <strong style={{ color: isDark ? '#F5F5F7' : '#1C1C1E' }}>{entry.changedBy}</strong>
                  </Typography>

                  {/* Diff rows */}
                  {Object.entries(entry.diff).map(([field, { before, after }]) => (
                    <DiffRow key={field} field={field} before={before} after={after} changeType={entry.changeType} />
                  ))}
                </Card>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
}
